/**
 * The AI workforce, as the homepage presents it.
 *
 * THE ROSTER IS THE SERVER'S. `src/domain/agent/agents.ts` holds AGENTS, which
 * is what the product actually runs on — the skills the assistant may reach, the
 * tag every detector carries, and the `live` flag that decides whether a name
 * has anything behind it. This file is the marketing face of that same list, and
 * `tests/aiworkforce.test.ts` reads both as text and fails if they disagree on a
 * name or on a status.
 *
 * Text rather than an import, following tests/navigation.test.ts: the web app
 * compiles under a different module resolution than the server, and pulling one
 * into the other's programme costs more than it proves.
 *
 * WHY THE PARITY TEST EARNS ITS KEEP. The status dot here is a public claim
 * about what a paying visitor will find when they sign up. The roster's own
 * header puts it plainly: "a name with nothing behind it is a claim, and the
 * house rule is that a claim gets a marker." A hand-kept copy of fourteen
 * statuses on a marketing page would disagree with the product within a
 * fortnight, and it would disagree in the flattering direction — which is the
 * failure the Coming-soon markers exist to prevent.
 *
 * So: to change what the homepage says an agent does, edit the blurb here. To
 * change whether it says an agent is running, ship the agent.
 */

/** Groups the cards by the part of the business they work in. */
export type AgentCategory = 'property' | 'finance' | 'tenant' | 'operations' | 'intelligence';

export interface WorkforceCard {
  /** Matches AgentCode in src/domain/agent/agents.ts, or 'assistant'. */
  code: string;
  /** Must match AGENTS[code].name exactly. Pinned by the parity test. */
  name: string;
  /** Must match AGENTS[code].live exactly. Pinned by the parity test. */
  live: boolean;
  category: AgentCategory;
  glyph: string;
  /** One line, on the card. */
  short: string;
  /** Two or three things a person can actually ask it for. Shown on hover. */
  actions: readonly string[];
  /** The expanded panel: what it watches, and what that lets it tell you. */
  monitors: string;
  understands: string;
}

export const CATEGORY_LABEL: Record<AgentCategory, string> = {
  property: 'Property',
  finance: 'Finance',
  tenant: 'Tenant',
  operations: 'Operations',
  intelligence: 'Intelligence',
};

/*
 * Every `short`, `actions`, `monitors` and `understands` below describes
 * behaviour somebody could go and observe on their own account. That is the same
 * bar the section it replaces was held to, and the reason its two Coming-soon
 * markers came off on the day the detectors behind them shipped rather than the
 * day the copy was written.
 */
export const WORKFORCE: readonly WorkforceCard[] = [
  {
    code: 'property_manager',
    name: 'AI Property Manager',
    live: true,
    category: 'property',
    glyph: '◎',
    short: 'Coordinates portfolio operations',
    actions: ['What needs my attention today?', 'What should I focus on this week?'],
    monitors: 'Arrears, vacancies, expiring leases, open maintenance and the records behind them, across every property you hold.',
    understands: 'Which of those matters most right now, so the day starts with a list rather than a search.',
  },
  {
    code: 'collections',
    name: 'AI Collections',
    live: true,
    category: 'finance',
    glyph: '₪',
    short: 'Monitors rent and arrears',
    actions: ['Which tenants are repeat late payers?', 'Review arrears'],
    monitors: 'Six months of settled charges against each tenancy’s own due date plus the grace you configured.',
    understands: 'The tenant who always pays but always late — someone who settles on the 20th every month and so never appears in arrears on the day you look.',
  },
  {
    code: 'finance',
    name: 'AI Finance',
    live: true,
    category: 'finance',
    glyph: '◈',
    short: 'Analyses financial performance',
    actions: ['Why did expenses increase?', 'How is collection tracking?'],
    monitors: 'Expenses by property and category against their own trailing history, and collection rate month on month.',
    understands: 'Which property moved and by how much, so a rise in the total has a name attached to it.',
  },
  {
    code: 'maintenance',
    name: 'AI Maintenance',
    live: true,
    category: 'property',
    glyph: '⚙',
    short: 'Tracks requests and work orders',
    actions: ['Which issues keep recurring?', 'Show jobs that were reopened'],
    monitors: 'Every request by unit and category, and how often a job marked finished comes back.',
    understands: 'The difference between a unit with bad luck and a unit with an unfixed fault — the second one bills you twice.',
  },
  {
    code: 'lease',
    name: 'AI Lease & Compliance',
    live: true,
    category: 'property',
    glyph: '✎',
    short: 'Monitors leases and obligations',
    actions: ['Which leases need attention?', 'What rent increases are due?'],
    monitors: 'Expiry dates without a renewal drafted, and every escalation clause with a date on it.',
    understands: 'What the rent becomes and when — quoting the figure the invoice will actually carry, not a second opinion of it.',
  },
  {
    code: 'vacancy',
    name: 'AI Vacancy & Occupancy',
    live: true,
    category: 'property',
    glyph: '▦',
    short: 'Tracks occupancy and vacancies',
    actions: ['Why is this unit still vacant?', 'Show units empty over 30 days'],
    monitors: 'How long each empty unit has been empty, and whether it is advertised.',
    understands: 'That an empty unit is lost revenue with a date attached, not a row in a status column.',
  },
  {
    code: 'tenant_experience',
    name: 'AI Tenant Assistant',
    live: true,
    category: 'tenant',
    glyph: '☖',
    short: 'Supports tenant requests',
    actions: ['What do I owe?', 'How do I report a repair?'],
    monitors: 'A tenant’s own charges, payments, receipts, documents and open requests — and nothing belonging to anybody else.',
    understands: 'What the balance is made of, so the answer is the statement rather than a phone call to the landlord.',
  },
  {
    code: 'owner_advisor',
    name: 'AI Owner Advisor',
    live: true,
    category: 'intelligence',
    glyph: '◫',
    short: 'Delivers owner insights',
    actions: ['How are my properties performing?', 'Which one should I look at?'],
    monitors: 'Income, expenses and remittance for the properties an owner actually owns, scoped by the database itself.',
    understands: 'Where the money went and which property is the reason, without exposing one owner’s figures to another.',
  },
  {
    code: 'accountant',
    name: 'AI Property Accountant',
    live: true,
    category: 'finance',
    glyph: '▤',
    short: 'Monitors property accounting',
    actions: ['Are there duplicate payments?', 'Show unallocated money'],
    monitors: 'Payments that look like the same payment twice, money received against no charge, and credit sitting on an account.',
    understands: 'Which of those is a reconciliation error and which is money a tenant is owed.',
  },
  {
    code: 'data_quality',
    name: 'AI Data Quality',
    live: true,
    category: 'intelligence',
    glyph: '◉',
    short: 'Protects data accuracy',
    actions: ['What records need fixing?', 'Who has no contact details?'],
    monitors: 'Tenants with no email or ID, properties with no address, units with no rent, tenancies with no lease on file.',
    understands: 'That a missing phone number is not untidiness — it is a receipt that will never arrive.',
  },
  {
    code: 'operations',
    name: 'AI Operations',
    live: true,
    category: 'operations',
    glyph: '✦',
    short: 'Coordinates daily operations',
    actions: ['What is outstanding right now?', 'Where do I go to fix this?'],
    monitors: 'The work queued across the portfolio and where each piece of it is handled.',
    understands: 'How to turn "what do I need to do" into the actual screen, with the record already selected.',
  },
  {
    code: 'communications',
    name: 'AI Communications',
    live: true,
    category: 'tenant',
    glyph: '✉',
    short: 'Manages communications',
    actions: ['Who needs to hear from me?', 'Remind everyone who is behind'],
    monitors: 'Who is owed a receipt, a reminder or a statement, and who has already had one recently.',
    understands: 'Who to contact and why. Ask it to chase the arrears and it shows you every tenant and what they owe first — nothing is sent until you confirm it. Reminders reach tenants by email and in their portal today; text messages wait on an SMS key.',
  },
  {
    code: 'process_advisor',
    name: 'AI Business Advisor',
    live: true,
    category: 'operations',
    glyph: '◆',
    short: 'Identifies business opportunities',
    actions: ['I have a tenant moving in tomorrow', 'Renew a lease', 'Move a tenant out'],
    monitors: 'The move-in, move-out and renewal checklists, evaluated against the records rather than from memory.',
    understands: 'Which step has genuinely been done and which was skipped — the deposit, the inspection, the portal invite, the first invoice.',
  },
  {
    code: 'tax',
    name: 'AI Tax (MRI)',
    live: true,
    category: 'finance',
    glyph: '%',
    short: 'Keeps the monthly rental income return on time',
    actions: ['How much MRI do I owe this month?', 'When is my rental income tax due?'],
    monitors: 'Rent received each month and the filing date for the Monthly Rental Income return.',
    understands: 'The figure is gross rent received, at your own rate, and it is due by the 20th. It shows the numbers; filing on iTax stays with you.',
  },
  {
    code: 'onboarding',
    name: 'AI Onboarding Coach',
    live: true,
    category: 'operations',
    glyph: '➜',
    short: 'Gets a new account running',
    actions: ['What should I set up next?', 'Am I fully set up?'],
    monitors: 'Which setup steps are done: properties, units, tenants, tenancies, rent and payment settings.',
    understands: 'The order a landlord actually works in, and takes you straight to the next step instead of a help page.',
  },
  {
    code: 'move_out',
    name: 'AI Move-out Assistant',
    live: true,
    category: 'tenant',
    glyph: '⇥',
    short: 'Closes a tenancy out properly',
    actions: ['Prepare the move-out checklist for a tenant', 'Start a move-out'],
    monitors: 'What the leaving tenant owes, the deposit held, and the steps still open.',
    understands: 'Inspection, deductions, refund, final statement and ending the tenancy, in that order, for the one tenant who is leaving.',
  },
  {
    code: 'control_panel',
    name: 'AI Control Panel',
    live: true,
    category: 'operations',
    glyph: '▣',
    short: 'Oversees the AI workforce',
    actions: ['What is waiting in the queues?', 'Did any job fail last night?'],
    monitors: 'NyumbaSuite’s own operations — approval queues, unconfirmed subscription payments, signups that went quiet, scheduled jobs that failed.',
    understands: 'How the platform itself is running. It reads nothing about any landlord’s business; the database refuses it.',
  },
  {
    /*
     * Not an agent, and listed last for that reason. The agents watch without
     * being asked; this is the one you talk to. The headline counts them
     * separately — "14 AI agents and 1 AI assistant" — so the card carries no
     * agent code and the parity test skips it.
     */
    code: 'assistant',
    name: 'AI Personal Assistant',
    live: true,
    category: 'intelligence',
    glyph: '✧',
    short: 'Helps users get things done',
    actions: ['Ask anything about your portfolio', 'Take me to the right screen'],
    monitors: 'Nothing on its own. It answers from your live records, through the same permissions your login already has.',
    understands: 'The question you actually asked — and it answers only from a tool it called, so a figure it quotes is one you can go and check.',
  },
];

/** The headline counts agents and the assistant separately, so the page does too. */
export const AGENT_COUNT = WORKFORCE.filter((c) => c.code !== 'assistant').length;

/**
 * HOW THE FIFTEEN ARE ARRANGED, and why they are not simply a grid.
 *
 * Fifteen equal cards scattered around a hub is fifteen things to read in no
 * order, and every one of them had to carry its own category label to mean
 * anything — which put the same five words on the page fifteen times and a
 * legend underneath repeating them a sixteenth. That is the clutter.
 *
 * Grouping fixes it at the root: the cards sit in five named teams, so the
 * category is said ONCE per team instead of once per card, the eye gets five
 * things to take in rather than fifteen, and the shape of the workforce —
 * four on property, three on finance — becomes visible rather than inferred.
 *
 * The split is by weight, not by meaning: seven cards left, eight right, so the
 * core sits in the optical centre of a balanced spread.
 */
export const COLUMNS: { left: AgentCategory[]; right: AgentCategory[] } = {
  left: ['property', 'finance'],
  right: ['operations', 'intelligence', 'tenant'],
};

/** The agents of one team, in roster order. */
export function agentsIn(category: AgentCategory): WorkforceCard[] {
  return WORKFORCE.filter((c) => c.category === category);
}
